import React, { useState, useRef, useEffect } from 'react';
import { Icons } from '../Icons';
import { displayDomain } from '../../utils';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalysisModeSwitch from '../common/AnalysisModeSwitch';

const LoadingText = ({ domain, t }) => {
  const [textIndex, setTextIndex] = useState(0);
  const cleanDomain = domain?.replace(/^https?:\/\//, '').replace(/\/$/, '') || 'domain';
  
  const loadingTexts = [
    t('searchForm.loadingAnalyzing'),
    `${t('searchForm.loadingDownloading')} ${cleanDomain}...`,
    `💡 ${t('searchForm.loadingTipLcp')}`,
    `📊 ${t('searchForm.loadingTipBounce')}`,
    `🚀 ${t('searchForm.loadingTipFast')}`,
    t('searchForm.loadingTipImpact')
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % loadingTexts.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [loadingTexts.length]);
  
  return <span className="transition-opacity text-xs sm:text-sm md:text-base text-center leading-tight">{loadingTexts[textIndex]}</span>;
};

const getSourceOptions = (t) => [
  { value: 'crux', label: t('searchForm.cruxRealData'), desc: t('searchForm.cruxRealDataDesc'), icon: '📊' },
  { value: 'pagespeed', label: t('searchForm.pageSpeed'), desc: t('searchForm.pageSpeedDesc'), icon: '⚡' },
  { value: 'lab', label: t('searchForm.labInsights'), desc: t('searchForm.labInsightsDesc'), icon: '🔬', premium: true }
];

const getDeviceOptions = (t) => [
  { value: 'ALL', label: t('searchForm.allDevices') },
  { value: 'PHONE', label: t('searchForm.mobile') },
  { value: 'DESKTOP', label: t('searchForm.desktop') },
  { value: 'TABLET', label: t('searchForm.tablet') }
];

const getPagespeedDeviceOptions = (t) => [
  { value: 'BOTH', label: t('searchForm.bothDevices') },
  { value: 'PHONE', label: t('searchForm.mobile') },
  { value: 'DESKTOP', label: t('searchForm.desktop') }
];

const getLabDeviceOptions = (t) => [
  { value: 'mobile', label: t('searchForm.mobile') },
  { value: 'desktop', label: t('searchForm.desktop') }
];

const getLabThrottlingOptions = (t) => [
  { value: 'none', label: t('searchForm.noThrottling') },
  { value: 'fast3g', label: 'Fast 3G' },
  { value: 'slow3g', label: 'Slow 3G' }
];

const getLabCmpOptions = (t) => [
  { value: 'default', label: t('searchForm.withCmpBar') },
  { value: 'auto-consent', label: t('searchForm.autoConsent') }
];


const getLabModules = (t) => [
  { value: 'basic', label: t('searchForm.basicMetrics'), required: true, quickTool: false },
  { value: 'lighthouse', label: t('searchForm.lighthouse'), quickTool: false },
  { value: 'accessibility', label: t('searchForm.accessibility'), quickTool: false },
  { value: 'thirdParty', label: t('searchForm.thirdParty'), quickTool: false },
  { value: 'security', label: t('searchForm.security'), quickTool: true },
  { value: 'schema', label: t('searchForm.seoSchema'), quickTool: true },
  { value: 'techstack', label: t('searchForm.techStack'), quickTool: true },
  { value: 'dnsssl', label: t('searchForm.dnsSsl'), quickTool: true },
  { value: 'carbon', label: t('searchForm.carbon'), quickTool: true },
  { value: 'coverage', label: t('searchForm.coverage'), quickTool: false },
  { value: 'lazyloading', label: t('searchForm.lazyLoading'), quickTool: true },
  { value: 'resourcehints', label: t('searchForm.resourceHints'), quickTool: true },
  { value: 'cdncheck', label: t('searchForm.cdnCheck'), quickTool: true },
  { value: 'conversioncalc', label: t('searchForm.conversionCalc'), quickTool: true },
  { value: 'waterfall', label: t('searchForm.waterfall'), quickTool: true },
  { value: 'infrasavings', label: t('searchForm.infraSavings'), quickTool: true },
  { value: 'httpprotocol', label: t('searchForm.httpProtocol'), quickTool: true, premium: true },
  { value: 'competitor', label: t('searchForm.competitorBenchmark'), quickTool: true, premium: true },
  { value: 'animationfps', label: t('searchForm.animationFps'), quickTool: true, premium: true },
  { value: 'abtest', label: t('searchForm.abTest'), quickTool: true, premium: true },
  { value: 'gtm', label: t('searchForm.gtmAudit'), quickTool: true },
  { value: 'media', label: t('searchForm.mediaAudit'), quickTool: true },
  { value: 'accessibilityAudit', label: t('searchForm.accessibilityAudit'), quickTool: true },
  { value: 'statuspage', label: t('searchForm.statusPage'), quickTool: true },
  { value: 'uptime', label: t('searchForm.uptimeCheck'), quickTool: true },
  { value: 'redirects', label: t('searchForm.redirectChain'), quickTool: true },
  { value: 'brokenlinks', label: t('searchForm.brokenLinks'), quickTool: true },
  { value: 'scriptLifecycle', label: t('searchForm.scriptLifecycle'), quickTool: true }
];

const getTypeOptions = (t) => [
  { value: 'origin', label: t('searchForm.entireWeb') },
  { value: 'url', label: t('searchForm.thisPage') }
];

export const SearchForm = ({ 
  onAnalyze,
  onCompare,
  loading, 
  hasResults = false,
  compareMode = false,
  onCompareModeChange,
  onReset,
  currentDomain = '',
  borderAnimationEnabled = false,
  borderAnimSettings = { intensity: 0.85, speed: 3, color1: '#06b6d4', color2: '#8b5cf6' },
  bgAnimationEnabled = false,
  bgAnimSettings = { intensity: 0.12, speed: 8, color1: '#06b6d4', color2: '#8b5cf6' },
  selectedForCompare,
  setSelectedForCompare,
  historyAvailability,
  currentUser,
  userDomains = [],
  userDomainGroups = [],
  trackedOrigins = [],
  comparisonMode = 'current',
  setComparisonMode,
  availablePeriods = [],
  selectedPeriod,
  setSelectedPeriod,
  loadingPeriods = false,
  syncingHistory,
  syncProgress,
  batchSyncing = false,
  syncHistoryForDomain,
  toggleDomainForCompare,
  handleRemoveUserDomain,
  removingDomain,
  runComparison,
  viewMode = 'detailed',
  onViewModeChange,
  source: externalSource,
  onSourceChange
}) => {
  const { t } = useLanguage();
  const [input, setInput] = useState(currentDomain || '');
  const [internalSource, setInternalSource] = useState('crux');
  const source = externalSource !== undefined ? externalSource : internalSource;
  const setSource = (newSource) => {
    if (onSourceChange) onSourceChange(newSource);
    setInternalSource(newSource);
  };
  const [device, setDevice] = useState('ALL');
  const [analysisType, setAnalysisType] = useState('origin');
  
  useEffect(() => {
    if (currentDomain && !input) {
      setInput(currentDomain);
    }
  }, [currentDomain]);

  const [labThrottling, setLabThrottling] = useState('fast3g');
  const [labCmpMode, setLabCmpMode] = useState('default');
  const [labModules, setLabModules] = useState(['basic', 'lighthouse', 'accessibility', 'thirdParty', 'security', 'schema', 'techstack', 'dnsssl', 'carbon', 'coverage']);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const SOURCE_OPTIONS = getSourceOptions(t);
  const DEVICE_OPTIONS = getDeviceOptions(t);
  const PAGESPEED_DEVICE_OPTIONS = getPagespeedDeviceOptions(t);
  const LAB_DEVICE_OPTIONS = getLabDeviceOptions(t);
  const LAB_THROTTLING_OPTIONS = getLabThrottlingOptions(t);
  const LAB_CMP_OPTIONS = getLabCmpOptions(t);
  const LAB_MODULES = getLabModules(t);
  const TYPE_OPTIONS = getTypeOptions(t);
  
  useEffect(() => {
    if (currentDomain === '' || currentDomain === null) {
      setInput('');
      setSelectedForCompare([]);
    }
  }, [currentDomain, setSelectedForCompare]);

  const toggleLabModule = (module) => {
    if (module === 'basic') return;
    setLabModules(prev => 
      prev.includes(module) 
        ? prev.filter(m => m !== module)
        : [...prev, module]
    );
  };
  
    
  useEffect(() => {
    if (source === 'pagespeed' || source === 'lighthouse') {
      if (device === 'ALL' || device === 'TABLET') {
        setDevice('BOTH');
      }
    } else if (source === 'lab') {
      if (device !== 'mobile' && device !== 'desktop') {
        setDevice('mobile');
      }
    } else {
      if (device === 'BOTH' || device === 'mobile' || device === 'desktop') {
        setDevice('ALL');
      }
    }
  }, [source]);
  
  const isPageSpeedMode = source === 'pagespeed' || source === 'lighthouse';
  const isLabMode = source === 'lab';
  const deviceOptions = isLabMode 
    ? LAB_DEVICE_OPTIONS
    : isPageSpeedMode 
      ? PAGESPEED_DEVICE_OPTIONS
      : DEVICE_OPTIONS;
  
  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    
    let cleanedInput = input.trim();
    if (analysisType === 'origin') {
      cleanedInput = cleanedInput.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    } else {
      if (!cleanedInput.startsWith('http://') && !cleanedInput.startsWith('https://')) {
        cleanedInput = 'https://' + cleanedInput;
      }
    }
    
    const options = { source, device, userInitiated: true };
    if (source === 'lab') {
      options.throttling = labThrottling;
      options.cmpMode = labCmpMode;
      options.modules = labModules;
    }
    
    onAnalyze(cleanedInput, analysisType, options);
  };

  const handleExampleClick = (domain) => {
    if (compareMode) {
      addDomainToCompare(domain);
    } else {
      setInput(domain);
      setAnalysisType('origin');
    }
  };

  const handleLogoClick = () => {
    if (onReset) {
      onReset();
    }
    setInput('');
    setSelectedForCompare([]);
  };

  const normalizeDomain = (domain) => {
    if (!domain || typeof domain !== 'string') return '';
    let cleaned = domain.trim().toLowerCase();
    cleaned = cleaned.replace(/^https?:\/\//, '');
    cleaned = cleaned.replace(/\/.*$/, '');
    cleaned = cleaned.replace(/^www\./, '');
    return cleaned;
  };

  const getCanonicalDomain = (domain) => {
    return normalizeDomain(domain);
  };

  const isDomainSelected = (domain, selected) => {
    return selected?.some(d => getCanonicalDomain(d) === getCanonicalDomain(domain));
  };

  const addDomainToCompare = (domain = input) => {
    const normalized = normalizeDomain(domain);
    if (!normalized) return;
    
    const alreadySelected = selectedForCompare?.some(d => normalizeDomain(d) === normalized);
    if (!alreadySelected) {
      setSelectedForCompare(prev => [...prev, normalized]);
    }
    setInput('');
  };

  const removeDomainFromCompare = (domain) => {
    setSelectedForCompare(prev => prev.filter(d => d !== domain));
  };

  const handleCompareSubmit = () => {
    if (selectedForCompare.length < 2 || loading) return;
    onCompare?.(selectedForCompare, { source, device, userInitiated: true });
  };

  // Keep the comparison form identical whether users arrive directly or
  // after analyzing a domain (and before or after comparison results).
  if (hasResults && !compareMode) {
    return (
      <div className="mb-6 relative z-50">
        <div className="bg-slate-900/80 backdrop-blur border border-slate-700/50 rounded-xl p-3 max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-3">
            <AnalysisModeSwitch mode="analyze" onCompare={() => onCompareModeChange?.(true)} />
            
            <SourceTabs
              value={source}
              onChange={setSource}
              options={SOURCE_OPTIONS}
            />
            
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-500">{t('searchForm.device')}</span>
              <FilterDropdown
                value={device}
                onChange={setDevice}
                options={deviceOptions}
                className="min-w-[100px]"
                t={t}
              />
            </div>
            
            {source === 'lab' && (
              <>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500">{t('searchForm.network')}</span>
                  <FilterDropdown
                    value={labThrottling}
                    onChange={setLabThrottling}
                    options={LAB_THROTTLING_OPTIONS}
                    className="min-w-[100px]"
                    t={t}
                  />
                </div>
                
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500">{t('searchForm.cmp')}</span>
                  <FilterDropdown
                    value={labCmpMode}
                    onChange={setLabCmpMode}
                    options={LAB_CMP_OPTIONS}
                    className="min-w-[120px]"
                    t={t}
                  />
                </div>
              </>
            )}
          </div>
          
          {source === 'lab' && (
            <div className="w-full mb-2">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-xs text-slate-600 dark:text-slate-500">{t('searchForm.modules')}</span>
                <button
                  type="button"
                  onClick={() => setLabModules(LAB_MODULES.map(m => m.value))}
                  className="px-2 py-0.5 text-[10px] rounded border border-green-400/50 bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-all"
                >
                  {t('searchForm.selectAll')}
                </button>
                <button
                  type="button"
                  onClick={() => setLabModules(['basic'])}
                  className="px-2 py-0.5 text-[10px] rounded border border-red-400/50 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                >
                  {t('searchForm.deselectAll')}
                </button>
              </div>
              <div className="overflow-x-auto pb-2 -mx-3 px-3 md:mx-0 md:px-0 md:overflow-visible">
                <div className="flex flex-wrap justify-center gap-1.5 min-w-max md:min-w-0">
                  {LAB_MODULES.map(mod => (
                    <button
                      key={mod.value}
                      type="button"
                      onClick={() => toggleLabModule(mod.value)}
                      disabled={mod.required}
                      className={`px-2 py-1 text-[11px] rounded-full border transition-all whitespace-nowrap ${
                        labModules.includes(mod.value)
                          ? mod.premium 
                            ? 'bg-purple-100 dark:bg-purple-500/20 border-purple-300 dark:border-purple-400/50 text-purple-700 dark:text-purple-300'
                            : 'bg-cyan-100 dark:bg-cyan-500/20 border-cyan-300 dark:border-cyan-400/50 text-cyan-700 dark:text-cyan-300'
                          : 'bg-slate-100 dark:bg-slate-800/50 border-slate-300 dark:border-slate-600/50 text-slate-600 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
                      } ${mod.required ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {mod.label}
                      {mod.premium && <span className="ml-1 text-[9px] bg-purple-500/30 text-purple-300 px-1 rounded">PRO</span>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap items-center justify-center gap-3 mb-3">
            {/* View Mode Toggle - Executive/Detailed - hide for Lab mode */}
            {!isLabMode && (
              <div className="inline-flex items-center bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-lg p-0.5 border border-cyan-400/50">
                <button
                  type="button"
                  onClick={() => onViewModeChange?.('executive')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                    viewMode === 'executive'
                      ? 'bg-cyan-500 text-white shadow-lg'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <Icons.Chart size={14} />
                  {t('searchForm.executive')}
                </button>
                <button
                  type="button"
                  onClick={() => onViewModeChange?.('detailed')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${
                    viewMode === 'detailed'
                      ? 'bg-purple-500 text-white shadow-lg'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <Icons.Code size={14} />
                  {t('searchForm.detailed')}
                </button>
              </div>
            )}
          </div>
          
          <form onSubmit={handleSubmit} className="flex flex-col gap-2">
              <div className="flex items-stretch">
                <TypeDropdown
                  value={analysisType}
                  onChange={setAnalysisType}
                  options={TYPE_OPTIONS}
                />
                
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={currentDomain || t('searchForm.enterDomain')}
                  className="flex-1 px-4 py-3 md:px-6 md:py-6 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-500 border-2 border-l-0 border-slate-200 dark:border-slate-700 rounded-r-2xl focus:outline-none text-xl md:text-2xl font-bold min-w-0 shadow-sm"
                  disabled={loading}
                />
              </div>
              
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="w-full px-4 py-3 bg-gradient-to-r from-fuchsia-500 to-violet-600 hover:from-fuchsia-400 hover:to-violet-500 text-white font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg text-sm shadow-lg shadow-fuchsia-500/30"
              >
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>{t('searchForm.analyzing')}</span>
                  </>
                ) : (
                  <>
                    {t('searchForm.runAudit')}
                    <Icons.Lightning size={14} />
                  </>
                )}
              </button>
              
              {currentUser && userDomains.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs">
                  <span className="text-slate-500">{t('searchForm.myDomains')}</span>
                  {userDomains.slice(0, 5).map(d => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => setInput(d.origin)}
                      disabled={loading}
                      aria-pressed={input === d.origin}
                      className={`cv-domain-chip px-2 py-0.5 rounded text-xs border transition-all ${
                        input === d.origin
                          ? 'bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-slate-200 border-slate-400 dark:border-slate-500'
                          : 'bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600 hover:border-slate-400'
                      } disabled:opacity-50`}
                    >
                      {displayDomain(d.origin)}
                    </button>
                  ))}
                  {userDomains.length > 5 && (
                    <span className="text-slate-500">+{userDomains.length - 5}</span>
                  )}
                </div>
              )}
              
          </form>
        </div>
      </div>
    );
  }
  
  const getOptionsLabel = () => {
    const parts = [];
    if (compareMode) {
      parts.push(t('searchForm.domainComparison'));
    } else {
      parts.push(t('searchForm.webAnalysis'));
    }
    const sourceLabel = SOURCE_OPTIONS.find(s => s.value === source)?.label || '';
    if (sourceLabel) parts.push(sourceLabel);
    const deviceLabel = deviceOptions.find(d => d.value === device)?.label || '';
    if (deviceLabel) parts.push(deviceLabel);
    return parts.join(' • ');
  };

  return (
    <div className={hasResults ? "mb-8" : "mb-4"}>
      <div 
        className={`${hasResults ? 'animated-border' : ''} ${!borderAnimationEnabled ? 'border-static' : ''} ${bgAnimationEnabled ? 'animated-bg-gradient' : ''} ${hasResults ? 'p-3 md:p-4 mb-4 bg-slate-900/50 backdrop-blur-xl border border-white/10 shadow-2xl' : 'p-4 md:p-6 mb-6'} relative z-20 rounded-2xl max-w-4xl mx-auto`}
        style={{
          ...(borderAnimationEnabled ? {
            '--border-color1': borderAnimSettings.color1,
            '--border-color2': borderAnimSettings.color2,
            '--border-color1-alt': borderAnimSettings.color1 + 'cc',
            '--border-color2-alt': borderAnimSettings.color2 + 'cc',
            '--border-speed': `${borderAnimSettings.speed}s`,
            '--border-opacity': borderAnimSettings.intensity
          } : {}),
          ...(bgAnimationEnabled ? {
            '--bg-anim-color1': `rgba(${parseInt(bgAnimSettings.color1.slice(1,3),16)}, ${parseInt(bgAnimSettings.color1.slice(3,5),16)}, ${parseInt(bgAnimSettings.color1.slice(5,7),16)}, ${bgAnimSettings.intensity})`,
            '--bg-anim-color2': `rgba(${parseInt(bgAnimSettings.color2.slice(1,3),16)}, ${parseInt(bgAnimSettings.color2.slice(3,5),16)}, ${parseInt(bgAnimSettings.color2.slice(5,7),16)}, ${bgAnimSettings.intensity})`,
            '--bg-anim-speed': `${bgAnimSettings.speed}s`
          } : {})
        }}
      >
        <div className="flex justify-center mb-4">
          <AnalysisModeSwitch mode={compareMode ? 'compare' : 'analyze'}
            onAnalyze={() => onCompareModeChange?.(false)}
            onCompare={() => onCompareModeChange?.(true)} />
        </div>
        <div className="flex justify-center mb-4">
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className="group flex items-center gap-3 px-5 py-2.5 rounded-xl bg-gradient-to-r from-slate-100 to-slate-50 dark:from-slate-800/80 dark:to-slate-700/60 border border-slate-200 dark:border-slate-600/50 hover:border-blue-400 dark:hover:border-cyan-400/50 transition-all shadow-sm hover:shadow-md"
          >
            <Icons.Settings size={16} className="text-slate-500 dark:text-cyan-400 group-hover:rotate-90 transition-transform duration-300" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
              {getOptionsLabel()}
            </span>
            <Icons.ChevronDown 
              size={16} 
              className={`text-slate-400 dark:text-slate-500 transition-transform duration-200 ${showAdvancedOptions ? 'rotate-180' : ''}`} 
            />
          </button>
        </div>
        
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showAdvancedOptions ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="pb-4 space-y-4">
            <SourceTabs
              value={source}
              onChange={setSource}
              options={SOURCE_OPTIONS}
            />
            
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-600 dark:text-white/60 font-medium">{t('searchForm.device')}</span>
                <div className="inline-flex bg-slate-100 dark:bg-slate-800/60 rounded-lg p-1 border border-slate-200 dark:border-slate-700/50">
                  {deviceOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setDevice(opt.value)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        device === opt.value
                          ? 'bg-white dark:bg-cyan-500/30 text-slate-900 dark:text-cyan-300 shadow-sm dark:shadow-lg dark:shadow-cyan-500/30 border border-slate-200 dark:border-cyan-400/70'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-cyan-300 border border-transparent'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {isLabMode && (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 dark:text-white/60 font-medium">{t('searchForm.network')}</span>
                    <FilterDropdown
                      value={labThrottling}
                      onChange={setLabThrottling}
                      options={LAB_THROTTLING_OPTIONS}
                      t={t}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-600 dark:text-white/60 font-medium">{t('searchForm.cmp')}</span>
                    <FilterDropdown
                      value={labCmpMode}
                      onChange={setLabCmpMode}
                      options={LAB_CMP_OPTIONS}
                      t={t}
                    />
                  </div>
                </>
              )}
            </div>
            
            {isLabMode && (
              <div className="w-full">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-xs text-slate-600 dark:text-white/60 font-medium">{t('searchForm.modules')}</span>
                  <button
                    type="button"
                    onClick={() => setLabModules(LAB_MODULES.map(m => m.value))}
                    className="px-2 py-0.5 text-[10px] rounded border border-green-400/50 bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-all"
                  >
                    {t('searchForm.selectAll')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLabModules(['basic'])}
                    className="px-2 py-0.5 text-[10px] rounded border border-red-400/50 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                  >
                    {t('searchForm.deselectAll')}
                  </button>
                </div>
                <div className="overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible">
                  <div className="flex flex-wrap justify-center gap-1.5 min-w-max md:min-w-0">
                    {LAB_MODULES.map(mod => (
                      <button
                        key={mod.value}
                        type="button"
                        onClick={() => toggleLabModule(mod.value)}
                        disabled={mod.required}
                        className={`px-2 py-1 text-[11px] rounded-full border transition-all whitespace-nowrap ${
                          labModules.includes(mod.value)
                            ? mod.premium 
                              ? 'bg-purple-100 dark:bg-purple-500/30 border-purple-300 dark:border-purple-400/50 text-purple-700 dark:text-purple-300'
                              : 'bg-cyan-100 dark:bg-cyan-500/30 border-cyan-300 dark:border-cyan-400/50 text-cyan-700 dark:text-cyan-300'
                            : 'bg-slate-100 dark:bg-slate-800/50 border-slate-300 dark:border-slate-600/50 text-slate-600 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
                        } ${mod.required ? 'opacity-70 cursor-not-allowed' : ''}`}
                      >
                        {mod.label}
                        {mod.premium && <span className="ml-1 text-[9px] bg-purple-500/30 text-purple-300 px-1 rounded">PRO</span>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {!isLabMode && !compareMode && (
              <div className="flex items-center justify-center gap-2">
                <span className="text-xs text-slate-600 dark:text-white/60 font-medium">{t('searchForm.analysisScope') || 'Rozsah:'}</span>
                <div className="inline-flex bg-slate-100 dark:bg-slate-800/60 rounded-lg p-1 border border-slate-200 dark:border-slate-700/50">
                  {TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setAnalysisType(opt.value)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                        analysisType === opt.value
                          ? 'bg-white dark:bg-cyan-500/30 text-slate-900 dark:text-cyan-300 shadow-sm dark:shadow-lg dark:shadow-cyan-500/30 border border-slate-200 dark:border-cyan-400/70'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-cyan-300 border border-transparent'
                      }`}
                    >
                      {opt.value === 'origin' ? <Icons.Globe size={14} /> : <Icons.FileText size={14} />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {!compareMode ? (
          <>
            <form onSubmit={handleSubmit} className="w-full max-w-3xl mx-auto">
              <input
                type="text"
                placeholder={analysisType === 'origin' ? t('searchForm.exampleDomain') : (t('searchForm.enterUrl') || 'https://example.com/page')}
                value={input}
                onChange={(e) => {
                  const newUrl = e.target.value;
                  setInput(newUrl);
                  if (newUrl.includes('/') && !newUrl.endsWith('/')) {
                    const pathPart = newUrl.replace(/^https?:\/\//, '').replace(/^www\./, '');
                    if (pathPart.includes('/') && pathPart.split('/').filter(p => p).length > 1) {
                      setAnalysisType('url');
                    }
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit(e)}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                className="w-full px-6 py-5 md:py-6 text-lg md:text-xl bg-white dark:bg-slate-800 rounded-xl border-2 border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 focus:border-blue-500 dark:focus:border-cyan-400 focus:ring-4 focus:ring-blue-500/20 dark:focus:ring-cyan-400/20 focus:outline-none text-slate-900 dark:text-white transition-all placeholder:text-slate-400 shadow-md hover:shadow-lg"
                disabled={loading}
              />
              
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className={`cv-analysis-submit w-full mt-4 px-4 md:px-8 py-5 rounded-xl font-bold text-lg md:text-xl transition-all flex items-center justify-center gap-2 md:gap-3 overflow-hidden ${
                  !input.trim() || loading 
                    ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 border border-slate-300 dark:border-slate-600 cursor-not-allowed' 
                    : 'bg-[#2563EB] hover:bg-[#1d4ed8] shadow-xl shadow-blue-600/40 hover:shadow-2xl hover:shadow-blue-600/50 text-white font-extrabold hover:scale-[1.02] active:scale-[0.99]'
                }`}
              >
                {loading ? (
                  <>
                    <span className="cv-loading-spinner w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <LoadingText domain={input} t={t} />
                  </>
                ) : (
                  <>
                    <Icons.TrendingDown size={22} />
                    <span>{t('searchForm.analyzeLosses')}</span>
                  </>
                )}
              </button>
              
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-5 text-sm text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  {t('searchForm.noCreditCard')}
                </span>
                <span className="flex items-center gap-1.5">
                  <svg className={`w-4 h-4 ${isLabMode ? 'text-yellow-500' : 'text-green-500'}`} fill="currentColor" viewBox="0 0 20 20">
                    {isLabMode ? (
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                    ) : (
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    )}
                  </svg>
                  {isLabMode ? t('searchForm.resultsIn1to3Min') : t('searchForm.resultsIn30Sec')}
                </span>
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  {isLabMode ? t('searchForm.syntheticBrowserTest') : t('searchForm.dataFromCrux')}
                </span>
              </div>
              
              {currentUser && userDomains.length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700/30">
                  <label className="block text-xs text-slate-500 dark:text-slate-400 mb-2 text-center font-medium">{t('searchForm.orSelectFromDomains')}</label>
                  <div className="flex flex-wrap justify-center gap-2">
                    {userDomains.slice(0, 8).map(d => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => setInput(d.origin)}
                        disabled={loading}
                        aria-pressed={input === d.origin}
                        className={`cv-domain-chip px-3 py-1.5 rounded-lg text-sm transition-all border ${
                          input === d.origin
                            ? 'bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-slate-200 border-slate-400 dark:border-slate-500'
                            : 'bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600 hover:border-slate-400'
                        } disabled:opacity-50`}
                      >
                        {displayDomain(d.origin)}
                      </button>
                    ))}
                    {userDomains.length > 8 && (
                      <span className="text-xs text-slate-500 self-center">+{userDomains.length - 8} {t('searchForm.moreItems')}</span>
                    )}
                  </div>
                </div>
              )}
            </form>
          </>
        ) : (
          <div id="comparison-selector" className="space-y-4 mb-4 scroll-mt-24">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">{t('searchForm.domainComparisonTitle')}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">{t('searchForm.domainComparisonDesc')}</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative group">
                <input
                  type="text"
                  placeholder={t('searchForm.enterCompetitorDomain')}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDomainToCompare())}
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  className="w-full px-4 py-3 pr-10 rounded-lg bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 text-slate-900 dark:text-white transition-all placeholder:text-slate-400 shadow-sm"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 cursor-help" title={t('searchForm.competitorDomainHint')}>
                  <Icons.Info size={16} className="text-slate-400 hover:text-blue-500 transition-colors" />
                </div>
              </div>
              <button
                onClick={() => addDomainToCompare()}
                disabled={!input.trim()}
                className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  input.trim() 
                    ? 'bg-[#2563EB] hover:bg-[#1d4ed8] text-white shadow-lg shadow-blue-500/30 hover:shadow-xl' 
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 border-2 border-dashed border-slate-400 dark:border-slate-500 cursor-not-allowed'
                }`}
              >
                + {t('searchForm.add')}
              </button>
            </div>

            {currentUser && (userDomains.length > 0 || userDomainGroups.length > 0) ? (
              <div className="space-y-4 mt-6 pt-4 border-t border-slate-300 dark:border-slate-700/30">
                {userDomainGroups.length > 0 && (
                  <div>
                    <label className="block text-xs text-slate-600 dark:text-slate-400 mb-2 font-medium">{t('searchForm.selectDomainGroup')}</label>
                    <div className="flex flex-wrap gap-2">
                      {userDomainGroups.map(g => {
                        const groupOrigins = g.members?.map(m => m.origin) || [];
                        const allSelected = groupOrigins.length > 0 && groupOrigins.every(o => isDomainSelected(o, selectedForCompare));
                        return (
                          <button
                            key={g.id}
                            onClick={() => {
                              if (allSelected) {
                                setSelectedForCompare(prev => prev.filter(o => !groupOrigins.some(go => getCanonicalDomain(go) === getCanonicalDomain(o))));
                              } else {
                                const newOrigins = groupOrigins.filter(o => !isDomainSelected(o, selectedForCompare));
                                setSelectedForCompare(prev => [...prev, ...newOrigins]);
                              }
                            }}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all border ${
                              allSelected
                                ? 'bg-blue-600 text-white border-blue-500 shadow-lg shadow-blue-500/30'
                                : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-500 hover:bg-blue-50 dark:hover:bg-blue-500/20 hover:border-blue-400 shadow-sm'
                            }`}
                            title={`${g.members?.length || 0} ${t('searchForm.domainsInGroup')}: ${groupOrigins.map(o => displayDomain(o)).join(', ')}`}
                          >
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: g.color || '#3b82f6' }}></div>
                            {g.name}
                            <span className="text-xs opacity-70">({g.members?.length || 0})</span>
                            {allSelected && <Icons.Check size={14} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <div>
                  <div className="border-t border-slate-300 dark:border-slate-700/50 pt-4 mt-2"></div>
                  <label className="block text-xs text-slate-600 dark:text-slate-400 mb-2 font-medium">{t('searchForm.orSelectFromHistory')} ({userDomains.length}):</label>
                  <div className="flex flex-wrap gap-2">
                    {userDomains.map(d => {
                      const originValue = d.origin;
                      const isSelected = isDomainSelected(originValue, selectedForCompare);
                      return (
                        <div key={d.id} className="relative group">
                          <button
                            onClick={() => toggleDomainForCompare?.(originValue)}
                            className={`px-3 py-1.5 rounded-lg text-sm transition-all pr-7 border ${
                              isSelected
                                ? 'bg-blue-600 text-white border-blue-500 shadow-lg shadow-blue-500/30'
                                : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-500 hover:bg-blue-50 dark:hover:bg-blue-500/20 hover:border-blue-400 shadow-sm'
                            }`}
                          >
                            {isSelected && <Icons.Check size={14} className="inline-block mr-1" />}
                            {displayDomain(originValue)}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRemoveUserDomain?.(d.originId); }}
                            disabled={removingDomain === d.originId}
                            className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-400 transition-all"
                            title={t('searchForm.removeFromList')}
                          >
                            {removingDomain === d.originId ? (
                              <span className="inline-block w-3 h-3 border border-slate-500 border-t-transparent rounded-full animate-spin"></span>
                            ) : (
                              <Icons.Close size={12} />
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : trackedOrigins.length > 0 && (
              <div className="mt-6 pt-4 border-t border-slate-300 dark:border-slate-700/30">
                <label className="block text-xs text-slate-600 dark:text-slate-400 mb-2 font-medium">{t('searchForm.orSelectFromTracked')} ({trackedOrigins.length}):</label>
                <div className="flex flex-wrap gap-2">
                  {trackedOrigins.map(o => {
                    const originValue = o.origin || o.url;
                    const isSelected = isDomainSelected(originValue, selectedForCompare);
                    return (
                      <button
                        key={o.id}
                        onClick={() => toggleDomainForCompare?.(originValue)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-all border ${
                          isSelected
                            ? 'bg-blue-600 text-white border-blue-500 shadow-lg shadow-blue-500/30'
                            : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-500 hover:bg-blue-50 dark:hover:bg-blue-500/20 hover:border-blue-400 shadow-sm'
                        }`}
                      >
                        {isSelected && <Icons.Check size={14} className="inline-block mr-1" />}
                        {displayDomain(originValue)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedForCompare.length > 0 && (
              <div className="bg-blue-50 dark:bg-slate-800/50 rounded-lg p-4 border border-blue-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-700 dark:text-slate-400 font-medium flex items-center gap-1.5">
                    {t('searchForm.selectedDomains')} ({selectedForCompare.length}):
                    <span className="cursor-help" title={t('searchForm.selectedDomainsHint')}>
                      <Icons.Info size={14} className="text-slate-400 hover:text-blue-500 transition-colors" />
                    </span>
                  </span>
                  <div className="flex items-center gap-3">
                    {selectedForCompare.length >= 2 && (
                      <button
                        onClick={runComparison || handleCompareSubmit}
                        disabled={loading || batchSyncing || (comparisonMode === 'historical' && !selectedPeriod)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2563EB] hover:bg-[#1d4ed8] rounded-md text-xs font-semibold text-white transition-all shadow-md shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Icons.Play size={12} />
                        Porovnat
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedForCompare([])}
                      className="text-xs text-red-500 hover:text-red-600 font-medium"
                    >
                      {t('searchForm.clearAll')}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedForCompare.map((origin, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm shadow-sm">
                      {displayDomain(origin)}
                      <button
                        onClick={() => toggleDomainForCompare?.(origin) || removeDomainFromCompare(origin)}
                        className="hover:text-red-200 ml-1"
                      >
                        <Icons.Close size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedForCompare.length >= 2 && (
              <div className="mb-4 p-3 bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-slate-300 dark:border-slate-700/50">
                <div className="flex items-center gap-4 mb-3">
                  <span className="text-sm text-slate-700 dark:text-slate-400 font-medium flex items-center gap-1.5">
                    {t('searchForm.dataLabel')}
                    <span className="cursor-help" title={t('searchForm.comparisonModeHint')}>
                      <Icons.Info size={14} className="text-slate-400 hover:text-blue-500 transition-colors" />
                    </span>
                  </span>
                  <div className="flex bg-white dark:bg-slate-700/50 rounded-lg p-0.5 border border-slate-300 dark:border-transparent shadow-sm">
                    <button
                      onClick={() => setComparisonMode?.('current')}
                      className={`px-3 py-1.5 rounded-md text-sm transition-all ${
                        comparisonMode === 'current'
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400'
                      }`}
                    >
                      {t('searchForm.current28Days')}
                    </button>
                    <button
                      onClick={() => setComparisonMode?.('historical')}
                      className={`px-3 py-1.5 rounded-md text-sm transition-all ${
                        comparisonMode === 'historical'
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400'
                      }`}
                    >
                      {t('searchForm.historical')}
                    </button>
                  </div>
                </div>
                
                {comparisonMode === 'historical' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-400">{t('searchForm.period')}</span>
                      {loadingPeriods ? (
                        <span className="text-sm text-slate-400">{t('searchForm.loading')}</span>
                      ) : availablePeriods.length > 0 ? (
                        <select
                          value={selectedPeriod || ''}
                          onChange={(e) => setSelectedPeriod?.(e.target.value)}
                          className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-sm text-white"
                        >
                          {availablePeriods.map((period, idx) => (
                            <option key={idx} value={period}>
                              {new Date(period).toLocaleDateString()}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-sm text-amber-400">{t('searchForm.noHistoricalData')}</span>
                      )}
                    </div>
                    
                    {historyAvailability && historyAvailability.length > 0 && (
                      <div className="p-2 bg-slate-900/50 rounded border border-slate-700/50">
                        <div className="text-xs text-slate-400 mb-2">{t('searchForm.historyAvailability')}</div>
                        <div className="space-y-1">
                          {historyAvailability.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between gap-2 text-xs">
                              <span className="text-slate-300 truncate max-w-[200px]">{item.origin}</span>
                              {item.hasHistory ? (
                                <span className="text-green-400 whitespace-nowrap">{item.weeksAvailable} {t('searchForm.weeksAvailable')}</span>
                              ) : item.blocked ? (
                                <span className="text-slate-400 text-xs">{t('searchForm.blocked')}</span>
                              ) : currentUser ? (
                                <button
                                  onClick={() => syncHistoryForDomain?.(item.origin)}
                                  disabled={syncingHistory !== null}
                                  className="px-2 py-0.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded text-xs transition-colors disabled:opacity-50"
                                >
                                  {syncingHistory === item.origin ? (
                                    <span className="flex items-center gap-1">
                                      <div className="w-2 h-2 border border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                                      {t('searchForm.loading')}
                                    </span>
                                  ) : (
                                    t('searchForm.loadHistory')
                                  )}
                                </button>
                              ) : (
                                <span className="text-slate-400 text-xs">{t('searchForm.unavailable')}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {syncProgress && (
              <div className={`p-3 rounded-lg mb-3 flex items-center gap-3 ${
                syncProgress.status === 'syncing' ? 'bg-blue-500/20 border border-blue-500/30' :
                syncProgress.status === 'complete' ? 'bg-green-500/20 border border-green-500/30' :
                syncProgress.status === 'partial' ? 'bg-yellow-500/20 border border-yellow-500/30' :
                'bg-red-500/20 border border-red-500/30'
              }`}>
                {syncProgress.status === 'syncing' && (
                  <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                )}
                {syncProgress.status === 'complete' && (
                  <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {syncProgress.status === 'partial' && (
                  <svg className="w-5 h-5 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                )}
                {syncProgress.status === 'error' && (
                  <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                <div className="flex-1">
                  <p className={`text-sm ${
                    syncProgress.status === 'syncing' ? 'text-blue-300' :
                    syncProgress.status === 'complete' ? 'text-green-300' :
                    syncProgress.status === 'partial' ? 'text-yellow-300' :
                    'text-red-300'
                  }`}>
                    {syncProgress.message}
                  </p>
                  {syncProgress.estimatedTime && (
                    <p className="text-xs text-slate-400 mt-1">{t('searchForm.estimatedTime')} {syncProgress.estimatedTime}</p>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={runComparison || handleCompareSubmit}
              disabled={loading || batchSyncing || selectedForCompare.length < 2 || (comparisonMode === 'historical' && !selectedPeriod)}
              className={`w-full px-8 py-4 rounded-xl font-bold text-lg transition-all ${
                loading || batchSyncing || selectedForCompare.length < 2 || (comparisonMode === 'historical' && !selectedPeriod)
                  ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-2 border-dashed border-slate-400 dark:border-slate-500 cursor-not-allowed'
                  : 'bg-[#2563EB] hover:bg-[#1d4ed8] shadow-xl shadow-blue-600/40 hover:shadow-2xl text-white'
              }`}
            >
              {loading || batchSyncing ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {batchSyncing ? t('searchForm.syncingHistory') : t('searchForm.comparing')}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2"><Icons.Scale size={18} /> {t('searchForm.compare')} {selectedForCompare.length} {selectedForCompare.length === 1 ? t('searchForm.domain1') : selectedForCompare.length < 5 ? t('searchForm.domains2to4') : t('searchForm.domains5plus')}</span>
              )}
            </button>
            
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <span className="text-xs text-slate-500 mr-2">{t('searchForm.addColon')}</span>
              {['alza.cz', 'mall.cz', 'czc.cz', 'datart.cz'].map((example) => (
                <button
                  key={example}
                  onClick={() => handleExampleClick(example)}
                  disabled={selectedForCompare.some(d => normalizeDomain(d) === normalizeDomain(example))}
                  className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                    selectedForCompare.some(d => normalizeDomain(d) === normalizeDomain(example))
                      ? 'bg-blue-500/20 text-blue-300 border border-blue-400/50 cursor-default'
                      : 'bg-white/10 dark:bg-slate-700 hover:bg-blue-500/20 border border-slate-300 dark:border-slate-600 hover:border-blue-400/50 text-slate-600 dark:text-slate-300 hover:text-blue-400 shadow-sm'
                  }`}
                >
                  {selectedForCompare.some(d => normalizeDomain(d) === normalizeDomain(example)) && <Icons.Check size={10} className="inline mr-1" />}
                  {example}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SourceTabs = ({ value, onChange, options }) => {
  return (
    <div className="flex flex-wrap justify-center gap-2 mb-4">
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={isActive}
            className={`cv-source-tab relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
              isActive
                ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-2 border-cyan-500/50 shadow-lg shadow-cyan-500/10'
                : 'bg-slate-800/50 text-slate-400 border border-slate-700 hover:border-slate-600 hover:text-slate-300 hover:bg-slate-700/50'
            }`}
          >
            <span className="text-base">{option.icon}</span>
            <div className="text-left">
              <div className={`font-semibold ${isActive ? 'text-cyan-300' : 'text-slate-300'}`}>
                {option.label}
              </div>
              <div className={`text-xs ${isActive ? 'text-cyan-400/70' : 'text-slate-500'}`}>
                {option.desc}
              </div>
            </div>
            {option.premium && (
              <span className="absolute -top-2 -right-2 px-1.5 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold rounded-full shadow-lg">
                PRO
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

const InlineDropdown = ({ value, onChange, options, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const selectedOption = options.find(o => o.value === value);
  
  return (
    <div ref={dropdownRef} className="relative flex items-center">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="h-full pl-4 pr-9 py-4 text-sm bg-slate-700 hover:bg-slate-600 focus:outline-none text-slate-100 cursor-pointer transition-colors flex items-center gap-2 rounded-l-lg border-r border-slate-600"
      >
        <span>{selectedOption?.label || t('admin.common.select')}</span>
      </button>
      <svg 
        className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-400 pointer-events-none transition-transform ${isOpen ? 'rotate-180' : ''}`}
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2.5"
      >
        <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      
      {isOpen && (
        <div className="absolute left-0 top-full z-[9999] min-w-full mt-1 py-1 rounded-lg bg-slate-800 border border-slate-600 shadow-xl overflow-hidden animate-slide-in" style={{animationDuration: '0.15s'}}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-sm text-left whitespace-nowrap transition-colors ${
                option.value === value
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'text-slate-300 hover:bg-cyan-500/10 hover:text-cyan-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TypeDropdown = ({ value, onChange, options, t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const selectedOption = options.find(o => o.value === value);
  
  return (
    <div ref={selectRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="h-full px-4 md:px-6 py-5 md:py-6 bg-white dark:bg-slate-800 text-cyan-600 dark:text-cyan-400 border-2 border-r-0 border-slate-300 dark:border-slate-600 rounded-l-xl focus:outline-none cursor-pointer text-base md:text-lg font-medium flex items-center gap-2 hover:border-slate-400 dark:hover:border-cyan-500/50 transition-colors shadow-md"
      >
        <span>{selectedOption?.label || t('admin.common.select')}</span>
        <Icons.ChevronDown 
          size={16} 
          className={`text-cyan-600 dark:text-cyan-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {isOpen && (
        <div className="absolute z-[9999] left-0 top-full mt-1 min-w-full py-1 rounded-xl bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 shadow-xl animate-slide-in" style={{animationDuration: '0.15s'}}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 md:px-6 py-3 text-base md:text-lg text-left whitespace-nowrap transition-colors ${
                option.value === value
                  ? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-cyan-500/10 hover:text-cyan-600 dark:hover:text-cyan-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const FilterDropdown = ({ value, onChange, options, className = "", t }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const selectedOption = options.find(o => o.value === value);
  
  return (
    <div ref={selectRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-1.5 text-sm rounded-lg bg-white dark:bg-slate-800/70 border border-slate-200 dark:border-slate-600 hover:border-blue-400 dark:hover:border-cyan-400/50 focus:border-blue-500 dark:focus:border-cyan-400 focus:outline-none text-slate-800 dark:text-white text-left flex items-center justify-between gap-2 transition-all min-w-[140px] shadow-sm"
      >
        <span>{selectedOption?.label || t('admin.common.select')}</span>
        <Icons.ChevronDown 
          size={14} 
          className={`text-slate-500 dark:text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      
      {isOpen && (
        <div className="absolute z-[9999] w-full mt-1 py-1 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 shadow-xl animate-slide-in" style={{animationDuration: '0.15s'}}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-sm text-left transition-colors ${
                option.value === value
                  ? 'bg-blue-50 dark:bg-cyan-500/20 text-blue-600 dark:text-cyan-400'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-cyan-500/10 hover:text-blue-600 dark:hover:text-cyan-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchForm;
